/**
 * Review and Submit Form Component
 * Final step of enhanced manufacturer registration
 */

import { FormEvent } from 'react';
import { Button } from '../ui';
import { RegistrationSession } from '../../services/registration.service';

interface ReviewSubmitFormProps {
  session: RegistrationSession;
  onSubmit: () => Promise<void>;
  onPrevious: () => void;
  saving: boolean;
}

export function ReviewSubmitForm({ session, onSubmit, onPrevious, saving }: ReviewSubmitFormProps) {
  const { data } = session;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onSubmit();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Review Your Information</h2>
        <p className="text-gray-600 mt-2">
          Please review all the information below before submitting your registration for approval.
        </p>
      </div>

      {/* Company Details */}
      {data.companyDetails && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Registered Name</label>
              <p className="text-sm text-gray-900">{data.companyDetails.registeredName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Entity Type</label>
              <p className="text-sm text-gray-900">{data.companyDetails.entityType}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date of Incorporation</label>
              <p className="text-sm text-gray-900">
                {new Date(data.companyDetails.dateOfIncorporation).toLocaleDateString()}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <p className="text-sm text-gray-900">{data.companyDetails.countryOfIncorporation}</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Registered Address</label>
              <p className="text-sm text-gray-900">
                {data.companyDetails.registeredAddress.addressLine1}
                {data.companyDetails.registeredAddress.addressLine2 && `, ${data.companyDetails.registeredAddress.addressLine2}`}
                <br />
                {data.companyDetails.registeredAddress.city}, {data.companyDetails.registeredAddress.state} - {data.companyDetails.registeredAddress.pincode}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tax Information */}
      {data.taxInformation && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">GSTIN</label>
              <p className="text-sm text-gray-900">{data.taxInformation.gstin}</p>
              <span className={`text-xs ${
                data.taxInformation.gstinValidationStatus === 'verified' ? 'text-green-600' : 
                data.taxInformation.gstinValidationStatus === 'failed' ? 'text-red-600' : 'text-yellow-600'
              }`}>
                {data.taxInformation.gstinValidationStatus === 'verified' && 'Verified ✓'}
                {data.taxInformation.gstinValidationStatus === 'failed' && 'Failed ✗'}
                {data.taxInformation.gstinValidationStatus === 'manual_review_required' && 'Manual Review Required'}
                {data.taxInformation.gstinValidationStatus === 'pending' && 'Pending Validation'}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">PAN</label>
              <p className="text-sm text-gray-900">{data.taxInformation.pan}</p>
              <span className={`text-xs ${
                data.taxInformation.panValidationStatus === 'verified' ? 'text-green-600' : 
                data.taxInformation.panValidationStatus === 'failed' ? 'text-red-600' : 'text-yellow-600'
              }`}>
                {data.taxInformation.panValidationStatus === 'verified' && 'Verified ✓'}
                {data.taxInformation.panValidationStatus === 'failed' && 'Failed ✗'}
                {data.taxInformation.panValidationStatus === 'manual_review_required' && 'Manual Review Required'}
                {data.taxInformation.panValidationStatus === 'pending' && 'Pending Validation'}
              </span>
            </div>
            {data.taxInformation.cin && (
              <div>
                <label className="block text-sm font-medium text-gray-700">CIN</label>
                <p className="text-sm text-gray-900">{data.taxInformation.cin}</p>
              </div>
            )}
            {data.taxInformation.udyamNumber && (
              <div>
                <label className="block text-sm font-medium text-gray-700">UDYAM Number</label>
                <p className="text-sm text-gray-900">{data.taxInformation.udyamNumber}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Authorized Signatory */}
      {data.authorizedSignatory && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Authorized Signatory</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <p className="text-sm text-gray-900">{data.authorizedSignatory.fullName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Designation</label>
              <p className="text-sm text-gray-900">{data.authorizedSignatory.designation}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <p className="text-sm text-gray-900">{data.authorizedSignatory.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Mobile</label>
              <p className="text-sm text-gray-900">{data.authorizedSignatory.mobileNumber}</p>
            </div>
          </div>
        </div>
      )}

      {/* Banking Details */}
      {data.bankingDetails && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Banking Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Bank Name</label>
              <p className="text-sm text-gray-900">{data.bankingDetails.bankName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Holder</label>
              <p className="text-sm text-gray-900">{data.bankingDetails.accountHolderName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Number</label>
              <p className="text-sm text-gray-900">
                {data.bankingDetails.accountNumber.replace(/\d(?=\d{4})/g, '*')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
              <p className="text-sm text-gray-900">{data.bankingDetails.ifscCode}</p>
            </div>
          </div>
        </div>
      )}

      {/* Brand Information */}
      {data.brandInformation && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Brand Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Brand Names</label>
              <p className="text-sm text-gray-900">{data.brandInformation.brandNames.join(', ')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Industry</label>
              <p className="text-sm text-gray-900">{data.brandInformation.industry}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Trademark Status</label>
              <p className="text-sm text-gray-900">{data.brandInformation.trademarkStatus}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Product Categories</label>
              <p className="text-sm text-gray-900">{data.brandInformation.productCategories.join(', ')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Status */}
      <div className="bg-green-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Status</h3>
        <div className="space-y-2">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-gray-900">Legal declarations accepted</span>
          </div>
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-gray-900">Terms of Service accepted</span>
          </div>
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-gray-900">Privacy Policy accepted</span>
          </div>
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-gray-900">Data processing consent provided</span>
          </div>
        </div>
      </div>

      {/* Final Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">What happens next?</h3>
            <div className="text-sm text-blue-700 mt-1">
              <p>• Your registration will be reviewed by our compliance team</p>
              <p>• You will receive an email confirmation once submitted</p>
              <p>• Review typically takes 2-3 business days</p>
              <p>• You may be contacted if additional documentation is required</p>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Form */}
      <form onSubmit={handleSubmit}>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onPrevious}
          >
            Previous
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="px-8 py-2 bg-green-600 hover:bg-green-700"
          >
            {saving ? 'Submitting...' : 'Submit Registration'}
          </Button>
        </div>
      </form>
    </div>
  );
}